import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { salesApi, unwrap } from "../api/resources";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useDayLock } from "../hooks/useDayLock";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { Button, Card, Empty, Field, Icon, Input, Loader, Modal, Progress, StatusPill } from "../components/ui";
import { ConfirmDialog, LockedNote, RecordActions } from "../components/actions";
import { CollectInstallmentModal } from "../components/CollectInstallment";
import { ProofLine } from "../components/PaymentFields";
import { SaleForm, saleMoneyChanged, salePayload, saleToForm } from "../components/forms";
import { InstallmentPlanEditor, InstallmentPlanTable } from "../components/InstallmentPlan";
import { COPY, hasInstallments } from "../utils/copy";
import { customPlanPayload, planMatchesRemaining, rowsFromPlan } from "../utils/plan";
import { calcSalePreview, formatDate, formatMoney, paidPercent } from "../utils/format";

export default function SaleDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { isBuilder } = useAuth();
  const toast = useToast();
  const { isClosed, anyClosed, isTodayClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planRows, setPlanRows] = useState([]);
  const [showPlanEdit, setShowPlanEdit] = useState(false);
  const [collectOpen, setCollectOpen] = useState(params.get("collect") === "1");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(saleToForm({}));
  const [editingPay, setEditingPay] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", note: "" });
  const [pending, setPending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = unwrap(await salesApi.get(id));
      setSale(data);
      setPlanRows(rowsFromPlan(data.installmentPlan));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (params.get("collect") === "1") setCollectOpen(true);
  }, [params]);

  const preview = useMemo(() => calcSalePreview(form), [form]);
  const saleLocked = sale ? isClosed(sale.date) : false;
  const paymentsLocked = anyClosed((sale?.payments || []).map((item) => item.date));
  const canCollect = isBuilder && sale?.status === "active" && sale?.remainingAmount > 0 && !isTodayClosed;
  const canEditPlan = isBuilder && sale?.status === "active" && sale?.remainingAmount > 0;

  const closeCollect = () => {
    setCollectOpen(false);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("collect");
      return next;
    });
  };

  const saveSale = async (event) => {
    event.preventDefault();
    if (preview.invalid) {
      toast.error("Down payment cannot be more than the total price");
      return;
    }
    const moneyChanged = !saleLocked && saleMoneyChanged(form, sale);
    const result = await run(
      () =>
        salesApi.update(
          id,
          salePayload(form, {
            includeProject: false,
            includeMoney: moneyChanged,
            includeDownPayment: moneyChanged && !hasInstallments(sale),
            includePlan: false,
          })
        ),
      "Sale updated."
    );
    if (result) {
      setEditOpen(false);
      reloadDays();
      load();
    }
  };

  const savePlan = async (event) => {
    event.preventDefault();
    if (!planMatchesRemaining(planRows, sale?.remainingAmount || 0)) {
      toast.error("Plan total must equal remaining.");
      return;
    }
    const result = await run(
      () => salesApi.updatePlan(id, { installments: customPlanPayload(planRows) }),
      "Remaining plan updated. Paid months stayed the same."
    );
    if (result) {
      setShowPlanEdit(false);
      load();
    }
  };

  const saveInstallment = async (event) => {
    event.preventDefault();
    if (!editingPay) return;
    const result = await run(
      () =>
        salesApi.updateInstallment(id, editingPay.id, {
          amount: Number(payForm.amount),
          note: payForm.note,
        }),
      "Installment updated. Remaining and daily book changed."
    );
    if (result) {
      setEditingPay(null);
      reloadDays();
      load();
    }
  };

  const confirmDelete = async () => {
    if (!pending) return;
    const result = await run(async () => {
      if (pending.type === "sale") return salesApi.remove(id);
      return salesApi.removeInstallment(id, pending.item.id);
    }, pending.type === "sale" ? "Sale deleted." : "Installment removed. Remaining went up.");

    if (!result) return;
    setPending(null);
    reloadDays();
    if (pending.type === "sale") navigate("/sales");
    else load();
  };

  if (loading) return <Loader />;
  if (!sale) return <Empty title="Sale not found" text="Go back to the sales list." />;

  return (
    <div className="stack sale-simple">
      <Link to="/sales" className="back-link">
        <Icon name="back" size={18} /> Back to sales
      </Link>

      <header className="page-head">
        <div>
          <p className="eyebrow">{sale.project?.name}</p>
          <h1>{sale.buyerName}</h1>
          <p className="muted">
            {sale.propertyName}
            {sale.buyerPhone ? ` · ${sale.buyerPhone}` : ""}
          </p>
        </div>
        <div className="head-actions">
          <StatusPill status={sale.status} />
          <Link className="btn btn-ghost btn-sm" to={`/sales/${id}/statement`}>
            Statement
          </Link>
          {canCollect ? (
            <Button className="btn-sm" onClick={() => setCollectOpen(true)}>
              Collect
            </Button>
          ) : null}
          <RecordActions
            disableDelete={saleLocked || paymentsLocked}
            onEdit={() => {
              setForm(saleToForm(sale));
              setEditOpen(true);
            }}
            onDelete={() => setPending({ type: "sale", item: sale })}
          />
        </div>
      </header>

      {saleLocked ? <LockedNote show /> : null}
      {isBuilder && isTodayClosed && sale.status === "active" ? (
        <div className="banner">Today's book is closed, so a new installment cannot be added.</div>
      ) : null}

      <Progress value={paidPercent(sale.receivedAmount, sale.totalAmount)} />

      <div className="stat-grid compact">
        <article className="stat stat-in">
          <span>Paid</span>
          <strong>{formatMoney(sale.receivedAmount)}</strong>
        </article>
        <article className="stat stat-now">
          <span>Remaining</span>
          <strong>{formatMoney(sale.remainingAmount)}</strong>
        </article>
        <article className="stat">
          <span>Monthly</span>
          <strong>{formatMoney(sale.monthlyInstallment)}</strong>
        </article>
      </div>

      <Card>
        <div className="card-head">
          <h2>Payments</h2>
        </div>
        <div className="list">
          <div className="list-row">
            <div>
              <b>Down payment</b>
              <span>{formatDate(sale.date)}</span>
              <ProofLine
                paymentType={sale.downPaymentType}
                referenceNumber={sale.downPaymentReference}
                image={sale.downPaymentImage}
                bank={sale.downPaymentBank || sale.bank}
              />
            </div>
            <strong className="plus">{formatMoney(sale.downPayment)}</strong>
          </div>
          {(sale.payments || []).length === 0 ? (
            <p className="muted">No installment yet. Use Collect on this page or the sales card.</p>
          ) : (
            sale.payments.map((payment, index) => {
              const locked = isClosed(payment.date);
              return (
                <div key={payment.id} className="list-row">
                  <div>
                    <b>Installment {index + 1}</b>
                    <span>
                      {formatDate(payment.date)}
                      {payment.note ? ` · ${payment.note}` : ""}
                      {locked ? " · Closed day" : ""}
                    </span>
                    <ProofLine
                      paymentType={payment.paymentType}
                      referenceNumber={payment.referenceNumber}
                      image={payment.image}
                      bank={payment.bank}
                    />
                  </div>
                  <strong className="plus">{formatMoney(payment.amount)}</strong>
                  <RecordActions
                    disableEdit={locked}
                    disableDelete={locked}
                    onEdit={() => {
                      setEditingPay(payment);
                      setPayForm({ amount: String(payment.amount), note: payment.note || "" });
                    }}
                    onDelete={() => setPending({ type: "installment", item: payment })}
                  />
                </div>
              );
            })
          )}
        </div>
      </Card>

      <Card>
        <div className="card-head">
          <h2>Plan</h2>
          {canEditPlan ? (
            <Button variant="ghost" className="btn-sm" onClick={() => setShowPlanEdit((open) => !open)}>
              {showPlanEdit ? "Hide editor" : "Change remaining"}
            </Button>
          ) : null}
        </div>
        <InstallmentPlanTable plan={sale.installmentPlan || []} />
        {showPlanEdit && canEditPlan ? (
          <form className="plan-save" onSubmit={savePlan}>
            <p className="muted">{COPY.planLocked}</p>
            <InstallmentPlanEditor
              rows={planRows}
              remaining={sale.remainingAmount}
              onChange={setPlanRows}
            />
            <Button type="submit" disabled={busy || !planMatchesRemaining(planRows, sale.remainingAmount)}>
              Save remaining plan
            </Button>
          </form>
        ) : null}
      </Card>

      <CollectInstallmentModal
        sale={sale}
        open={collectOpen}
        onClose={closeCollect}
        onSaved={load}
      />

      <Modal
        open={editOpen}
        title="Edit sale"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="sale-form" disabled={busy || preview.invalid}>
              Save
            </Button>
          </>
        }
      >
        <LockedNote show={saleLocked} />
        <SaleForm
          form={form}
          onChange={setForm}
          onSubmit={saveSale}
          showProject={false}
          showPlan={false}
          moneyLocked={saleLocked}
          downPaymentLocked={hasInstallments(sale)}
        />
      </Modal>

      <Modal
        open={Boolean(editingPay)}
        title="Edit installment"
        onClose={() => setEditingPay(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditingPay(null)}>
              Cancel
            </Button>
            <Button type="submit" form="pay-form" disabled={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="pay-form" className="form-grid" onSubmit={saveInstallment}>
          <Field label="Amount">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              required
              value={payForm.amount}
              onChange={(event) => setPayForm((prev) => ({ ...prev, amount: event.target.value }))}
            />
          </Field>
          <Field label="Note">
            <Input
              value={payForm.note}
              onChange={(event) => setPayForm((prev) => ({ ...prev, note: event.target.value }))}
            />
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.type === "sale" ? "Delete sale?" : "Delete installment?"}
        text={pending?.type === "sale" ? COPY.deleteSale : COPY.deleteInstallment}
        busy={busy}
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
